import { useRef, useEffect, useCallback } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { useDashboardTheme, ACCENT_COLOR, cardSx, panelHeaderSx } from '../utils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

function fmtLabel(bucket) {
  if (!bucket) return '';
  const d = new Date(bucket);
  if (isNaN(d.getTime())) return bucket;
  return d.toLocaleString('en-GB', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function ChartCard({ title, loading, t, children }) {
  return (
    <Card sx={cardSx}>
      <MDBox sx={panelHeaderSx(t)}>
        <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
          {title}
        </MDTypography>
      </MDBox>
      <MDBox px={{ xs: 2, sm: 3 }} pb={2} pt={1} sx={{ height: 260, position: 'relative' }}>
        {loading ? (
          <Skeleton variant='rectangular' height='100%' sx={{ borderRadius: '8px' }} />
        ) : (
          children
        )}
      </MDBox>
    </Card>
  );
}

/**
 * Persistent canvas-based chart that updates data in-place via chart.update()
 * instead of destroying/recreating on every poll.
 */
function PersistentChart({ type, labels, datasets, options }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const prevDataRef = useRef(null);

  // Create chart when canvas appears (handles loading→visible transition)
  useEffect(() => {
    if (!canvasRef.current || chartRef.current) return;
    chartRef.current = new ChartJS(canvasRef.current, {
      type,
      data: {
        labels: labels || [],
        datasets: (datasets || []).map((ds) => ({ ...ds }))
      },
      options: options ? JSON.parse(JSON.stringify(options)) : {}
    });
    prevDataRef.current = JSON.stringify({ labels, datasets });
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, []);

  // Update data in-place without destroy/recreate
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    // Skip if data hasn't actually changed (avoids unnecessary chart.update calls)
    const dataKey = JSON.stringify({ labels, datasets });
    if (prevDataRef.current === dataKey) return;
    prevDataRef.current = dataKey;

    chart.data.labels = labels || [];

    const incoming = datasets || [];
    for (let i = 0; i < incoming.length; i++) {
      if (chart.data.datasets[i]) {
        Object.assign(chart.data.datasets[i], incoming[i]);
      } else {
        chart.data.datasets.push({ ...incoming[i] });
      }
    }
    chart.data.datasets.length = incoming.length;

    // Deep-replace options (shallow assign misses nested scales/plugins)
    if (options) {
      chart.options = JSON.parse(JSON.stringify(options));
    }

    chart.update('none');
  }, [labels, datasets, options]);

  return <canvas ref={canvasRef} />;
}

function DashboardCharts({ netMetrics, loading }) {
  const t = useDashboardTheme();
  const blockRewards = netMetrics?.blockRewardsOverTime || [];
  const bidsOT = netMetrics?.bidsOverTime || [];
  const airdrops = netMetrics?.airdropsOverTime || [];
  const cumulative = netMetrics?.cumulativeNetOverTime || [];

  const buildOpts = useCallback(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: t.chartLabelColor, font: { size: 11 } } },
      tooltip: { mode: 'index', intersect: false }
    },
    scales: {
      x: { ticks: { color: t.chartLabelColor, font: { size: 10 }, maxRotation: 45 }, grid: { color: t.chartGridColor } },
      y: { ticks: { color: t.chartLabelColor, font: { size: 10 } }, grid: { color: t.chartGridColor } }
    }
  }), [t.chartLabelColor, t.chartGridColor]);

  // Stable refs for chart data — avoids re-creating arrays on every render
  const blockLabels = blockRewards.map((b) => fmtLabel(b.bucket));
  const blockDatasets = [{
    label: 'BTC',
    data: blockRewards.map((b) => b.totalBtc),
    backgroundColor: `${ACCENT_COLOR}99`,
    borderColor: ACCENT_COLOR,
    borderWidth: 1
  }];

  const bidsLabels = bidsOT.map((b) => fmtLabel(b.bucket));
  const bidsDatasets = [{
    label: 'Bid Count',
    data: bidsOT.map((b) => b.count),
    borderColor: '#6366f1',
    backgroundColor: 'rgba(99,102,241,0.15)',
    fill: true,
    tension: 0.3
  }];

  const airdropLabels = airdrops.map((b) => fmtLabel(b.bucket));
  const airdropDatasets = [{
    label: 'BTC Distributed',
    data: airdrops.map((b) => b.totalBtc),
    backgroundColor: 'rgba(31,166,120,0.6)',
    borderColor: '#1fa678',
    borderWidth: 1
  }];

  const isPositive = cumulative.length > 0 && cumulative[cumulative.length - 1].totalBtc >= 0;
  const cumulativeLabels = cumulative.map((b) => fmtLabel(b.bucket));
  const cumulativeDatasets = [{
    label: 'Cumulative Operator P&L BTC',
    data: cumulative.map((b) => b.totalBtc),
    borderColor: isPositive ? '#1fa678' : '#db3f57',
    backgroundColor: isPositive ? 'rgba(31,166,120,0.1)' : 'rgba(219,63,87,0.1)',
    fill: true,
    tension: 0.3
  }];

  const chartOpts = buildOpts();

  const noData = blockRewards.length === 0 && bidsOT.length === 0 && airdrops.length === 0 && cumulative.length === 0;

  if (!loading && noData) {
    return (
      <Card sx={cardSx}>
        <MDBox sx={panelHeaderSx(t)}>
          <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>Charts</MDTypography>
        </MDBox>
        <MDBox display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={5} gap={1}>
          <MDTypography variant='body2' color='text' sx={{ opacity: 0.45 }}>No time-series data available yet</MDTypography>
        </MDBox>
      </Card>
    );
  }

  return (
    <MDBox mb={3}>
      <MDBox display='flex' alignItems='center' gap={1.25} mb={2.5}>
        <MDBox sx={{ width: '3px', height: '18px', borderRadius: '2px', background: ACCENT_COLOR, flexShrink: 0 }} />
        <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', color: t.primaryText, fontSize: '0.95rem' }}>
          Performance Charts
        </MDTypography>
        <MDBox sx={{ flex: 1, height: '1px', background: t.divider, display: { xs: 'none', sm: 'block' } }} />
      </MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <ChartCard title='Block Rewards Over Time' loading={loading && blockRewards.length === 0} t={t}>
            <PersistentChart type='bar' labels={blockLabels} datasets={blockDatasets} options={chartOpts} />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title='Bids Over Time' loading={loading && bidsOT.length === 0} t={t}>
            <PersistentChart type='line' labels={bidsLabels} datasets={bidsDatasets} options={chartOpts} />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title='Airdrop Distributions Over Time' loading={loading && airdrops.length === 0} t={t}>
            <PersistentChart type='bar' labels={airdropLabels} datasets={airdropDatasets} options={chartOpts} />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title='Cumulative Operator P&L (BTC)' loading={loading && cumulative.length === 0} t={t}>
            <PersistentChart type='line' labels={cumulativeLabels} datasets={cumulativeDatasets} options={chartOpts} />
          </ChartCard>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default DashboardCharts;
